import React, { useEffect, useId, useRef, useState } from 'react';

// Presentation only: the coordinator supplies every deadline and validity decision.
export default function SessionTimeout({ state, onStaySignedIn, onSignOut, onRecheck, returnFocusRef }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const panel = useRef(null);
  const panelId = useId();
  const visible = Boolean(state && (state.warning || state.unverified || state.error));
  const seconds = Math.max(0, Math.ceil((state?.remainingMs || 0) / 1000));
  const countdown = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  const message = !visible ? '' : state.unverified
    ? 'Checking session validity. Your session has not been extended.'
    : `Your session deadline is approaching. Estimated time remaining: ${countdown}.`;
  const progress = state?.status === 'RENEWING' ? ' Renewing your session…'
    : state?.busy ? ' Please wait.' : '';
  const absoluteMessage = state?.approachingAbsolute
    ? 'The maximum authenticated lifetime is approaching. Renewal cannot extend it; you will need to sign in again.' : '';
  const summary = !visible ? 'Session verified' : state.error ? 'Session needs attention'
    : state.unverified ? 'Checking session' : state.status === 'RENEWING' ? 'Renewing session' : `Session expires in ${countdown}`;

  useEffect(() => { if (open) panel.current?.focus(); }, [open]);
  const close = () => {
    (visible ? trigger.current : returnFocusRef?.current)?.focus();
    setOpen(false);
  };

  return <div className="session-presentation min-w-0 flex-1 flex items-center justify-end gap-2"
    onKeyDown={event => { if (open && event.key === 'Escape') { event.preventDefault(); close(); } }}>
    <p className="sr-only" role="status" aria-live="polite">{message}{visible && progress}{visible && absoluteMessage}</p>
    <p className="sr-only" role="alert">{state?.error || ''}</p>
    {(visible || open) && <>
      <span aria-hidden="true" className="min-w-0 truncate text-sm">{summary}</span>
      <button ref={trigger} type="button" className="shrink-0 rounded border px-2 py-1 text-sm"
        aria-expanded={open} aria-controls={open ? panelId : undefined}
        onClick={() => open ? close() : setOpen(true)}>Session details</button>
    </>}
    {open && <section ref={panel} id={panelId} tabIndex={-1} aria-label="Session details"
      className="absolute right-0 top-full w-full max-w-md max-h-[min(24rem,60vh)] supports-[height:100dvh]:max-h-[min(24rem,60dvh)] overflow-y-auto overscroll-contain break-words border border-amber-300 bg-amber-50 p-4 text-slate-900 shadow-lg">
      <p>{visible ? message + progress : 'No current session warning.'}</p>
      {absoluteMessage && <p>{absoluteMessage}</p>}
      {state?.error && <p>{state.error}</p>}
      <div className="mt-2 flex flex-wrap gap-3">
        <button type="button" disabled={!state?.canRenew || state?.absoluteLimited} onClick={onStaySignedIn}>Stay signed in</button>
        <button type="button" disabled={!state?.unverified || state?.busy} onClick={onRecheck}>Check session</button>
        <button type="button" onClick={onSignOut}>Sign out</button>
        <button type="button" onClick={close}>Close session details</button>
      </div>
    </section>}
  </div>;
}
